import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth.service';
import { PermissionsService } from '../../core/services/permissions.service';
import { roleNameKey } from '../../core/models/role-metadata';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslatePipe],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss'
})
export class TopbarComponent {
  private translateSvc = inject(TranslateService);
  readonly auth        = inject(AuthService);
  readonly permissions = inject(PermissionsService);

  /**
   * Translation key for the role shown under the user's name in the dropdown.
   *
   * This used to be `isSystemAdmin ? sysadmin : admin`, which labelled every non-admin an
   * Administrator — a Full Read user read as one. The role is known now, so it is named. A role
   * missing from `ROLE_METADATA` yields null and the template falls back to the backend's own
   * English name rather than rendering a raw key.
   *
   * A key rather than a resolved string on purpose: `TranslatePipe` re-evaluates on a language
   * switch by itself, which keeps this clear of the `injectCurrentLanguage()` trap.
   */
  readonly roleLabelKey = computed<string | null>(() =>
    this.permissions.isSystemAdmin() ? 'user_role_system_admin' : roleNameKey(this.permissions.roleName())
  );

  open        = signal(false);
  currentLang = signal(this.translateSvc.currentLang ?? 'da');

  @HostListener('document:click')
  onDocumentClick() {
    this.open.set(false);
  }

  togglePanel(event: Event) {
    event.stopPropagation();
    this.open.update(v => !v);
  }

  /** The panel stops click bubbling, so navigation inside it has to close it explicitly. */
  closePanel() {
    this.open.set(false);
  }

  setLang(lang: string, event: Event) {
    event.stopPropagation();
    this.translateSvc.use(lang);
    this.currentLang.set(lang);
    localStorage.setItem('vf_lang', lang);
  }

  logout() {
    this.auth.logout();
  }
}
